import { Flex, Switch, Text } from "@chakra-ui/react";
import hj from "@hotjar/browser";
import { ChainIds, type Network, getChainFullIdFromNetwork } from "@umami/chains";
import { useAvailableMultichainNetworks, useIsChainEnabled } from "@umami/state";

import { DrawerContentWrapper } from "../DrawerContentWrapper";
import { useToggleNetwork } from "./useToggleNetwork";

import { NetworkSelectorSearchBar } from "@/components/NetworkSelector";
import { ChainIcon } from "@/components/Token/ChainIcon";
import { useFilter } from "@/hooks/useFilter";
import { useColor } from "@/styles/useColor";

export const NetworkMenu = () => {
  const color = useColor();

  const { networks } = useAvailableMultichainNetworks();
  const isChainEnabled = useIsChainEnabled();
  const toggleNetwork = useToggleNetwork();

  hj.stateChange("menu/network");

  // Sort networks so that mainnets are first
  const sortedNetworks = networks.toSorted((a, b) => {
    if (!a.isTestnet && b.isTestnet) {
      return -1;
    }

    if (a.isTestnet && !b.isTestnet) {
      return 1;
    }

    return 0;
  });

  const { setInput, data: filteredChains } = useFilter(
    sortedNetworks,
    (network, input) =>
      network.name.toLowerCase().includes(input) || network.chain.toLowerCase().includes(input)
  );

  const getChainName = (chain: Network) => {
    if (chain.chain === ChainIds.TEZOS) {
      // Match by exact `name` so "michelson shadownet" doesn't collapse into
      // "Tezos Shadownet" (both contain "shadownet").
      switch (chain.name) {
        case "mainnet":
          return "Tezos";
        case "shadownet":
          return "Tezos Shadownet";
        case "michelson":
          return "Tezos Michelson";
        case "michelson shadownet":
          return "Tezos Michelson Shadownet";
        case "previewnet":
          return "Tezos Previewnet";
      }

      return "Tezos";
    }

    return chain.name;
  };

  return (
    <DrawerContentWrapper
      actions={
        <Flex alignItems="center" gap="12px" marginTop="18px">
          <NetworkSelectorSearchBar onChange={setInput} />
          {/* TODO: Disable for now until we have a proper way to add networks */}
          {/* {isMobile ? (
            <IconButton
              aria-label="Add new network"
              icon={<PlusIcon />}
              onClick={() => openWith(<EditNetworkMenu />)}
              variant="primary"
            />
          ) : (
            <Button
              width="fit-content"
              minWidth="auto"
              padding="0 24px"
              onClick={() => openWith(<EditNetworkMenu />)}
              rightIcon={<PlusIcon />}
              variant="primary"
            >
              Add new
            </Button>
          )} */}
        </Flex>
      }
      title="Network"
    >
      <Flex flexDirection="column" gap="8px" padding="30px 0">
        {filteredChains.map(chain => (
          <Flex
            key={chain.name}
            alignItems="center"
            justifyContent="space-between"
            padding="8px 12px"
          >
            <Flex alignItems="center" gap="12px">
              <Flex alignItems="center" flex="0 0 auto">
                <ChainIcon chainId={chain.chain} />
              </Flex>
              <Text
                color={color("black")}
                fontWeight="600"
                data-testid={`network-name-${getChainFullIdFromNetwork(chain)}`}
                size="md"
              >
                {getChainName(chain)}
              </Text>
            </Flex>
            {/* TODO: Add a way to enable/disable chains */}
            <Switch
              data-testid={`network-switch-${getChainFullIdFromNetwork(chain)}`}
              isChecked={isChainEnabled(chain)}
              onChange={() => toggleNetwork(chain)}
            />
          </Flex>
        ))}
      </Flex>
    </DrawerContentWrapper>
  );
};
